import { useState, useEffect, useCallback } from 'react'
import { createFileRoute, useParams } from '@tanstack/react-router'
import { Container, Text, Paper, Stack, Divider, Modal, Button, Group } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { ResponsiveTitle } from '../../components/ResponsiveTitle/ResponsiveTitle'
import { PhotoUpload } from '../../components/PhotoUpload/PhotoUpload'
import { PhotoGallery } from '../../components/PhotoGallery/PhotoGallery'
import { PhotoLightbox } from '../../components/PhotoLightbox/PhotoLightbox'
import { useGuests } from '../../hooks/useGuests'
import { usePhotos } from '../../hooks/usePhotos'
import { useSiteContent } from '@/hooks/useSiteContent'
import type { PhotoForDisplay } from '../../lib/types'

export const Route = createFileRoute('/guest/logged_in/$pin/photos')({
  component: GuestPhotos,
})

function GuestPhotos() {
  const { content } = useSiteContent()
  const params = useParams({ from: '/guest/logged_in/$pin/photos' })
  const pin = params.pin
  const { getGuestByPin } = useGuests()
  const { fetchGuestPhotos, deletePhoto } = usePhotos()

  const [guestId, setGuestId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [lightboxOpened, setLightboxOpened] = useState(false)
  const [lightboxPhotos, setLightboxPhotos] = useState<PhotoForDisplay[]>([])
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [deleteModalOpened, setDeleteModalOpened] = useState(false)
  const [photoToDelete, setPhotoToDelete] = useState<PhotoForDisplay | null>(null)
  const [galleryRefreshKey, setGalleryRefreshKey] = useState(0)

  // Load guest data from PIN (parent already validated PIN exists)
  useEffect(() => {
    const loadGuest = async () => {
      if (!pin) {
        setLoading(false)
        return
      }

      setLoading(true)
      const guestData = await getGuestByPin(pin)

      if (guestData) {
        setGuestId(guestData.id)
      }
      setLoading(false)
    }

    loadGuest()
  }, [pin, getGuestByPin])

  const handleUploadSuccess = useCallback(() => {
    // Refresh gallery after successful upload
    setGalleryRefreshKey((prev) => prev + 1)
  }, [])

  const handlePhotoClick = useCallback(
    async (photo: PhotoForDisplay, index: number) => {
      // Load all photos for lightbox navigation
      if (guestId && pin) {
        const result = await fetchGuestPhotos(
          guestId,
          {
            page: 1,
            pageSize: 'all',
            sortBy: 'uploaded_at',
            sortDirection: 'desc',
          },
          pin
        )
        setLightboxPhotos(result.photos)
        // Find the index of clicked photo in the full list
        const fullIndex = result.photos.findIndex((p) => p.id === photo.id)
        setLightboxIndex(fullIndex >= 0 ? fullIndex : index)
        setLightboxOpened(true)
      }
    },
    [guestId, fetchGuestPhotos, pin]
  )

  const handleDeleteClick = useCallback((photo: PhotoForDisplay) => {
    setPhotoToDelete(photo)
    setDeleteModalOpened(true)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (!photoToDelete || !pin) return

    try {
      const success = await deletePhoto(photoToDelete.id, photoToDelete.storagePath, pin)

      if (success) {
        notifications.show({
          title: 'Erfolgreich gelöscht',
          message: 'Das Foto wurde erfolgreich gelöscht.',
          color: 'green',
        })

        // Refresh gallery by incrementing key
        setGalleryRefreshKey((prev) => prev + 1)
      } else {
        notifications.show({
          title: 'Fehler',
          message: 'Foto konnte nicht gelöscht werden.',
          color: 'red',
        })
      }
    } catch (error) {
      console.error('Delete error:', error)
      notifications.show({
        title: 'Fehler',
        message: 'Ein unerwarteter Fehler ist aufgetreten.',
        color: 'red',
      })
    } finally {
      setDeleteModalOpened(false)
      setPhotoToDelete(null)
    }
  }, [photoToDelete, deletePhoto, pin])

  if (loading || !pin || !guestId) {
    return null // Parent route handles loading/error states
  }

  return (
    <>
      <Container size="lg" py="xl">
        <Paper p="xl" shadow="sm" radius="md" bg="white">
          <Stack gap="xl">
            <ResponsiveTitle order={2}>
              {content?.ui.Hochzeitsfotos || 'Hochzeitsfotos'}
            </ResponsiveTitle>
            <Text c="dimmed" size="md" style={{ fontStyle: 'italic' }}>
              {content?.photos.description ||
                'Teilt jene Augenblicke mit uns, die diese festliche Hochzeit besonders wertvoll machen. Ladet jene Fotos empor, die euch besonders am Herzen liegen und die Erinnerung an diesen festlichen Tag bewahren.'}
            </Text>

            <Divider />
            <PhotoUpload guestId={guestId} guestPin={pin} onUploadSuccess={handleUploadSuccess} />
            <Divider />
            <PhotoGallery
              key={galleryRefreshKey}
              guestId={guestId}
              guestPin={pin}
              onPhotoClick={handlePhotoClick}
              onDeleteClick={handleDeleteClick}
              showDeleteButton={true}
            />

            {/* Photo Lightbox */}
            <PhotoLightbox
              opened={lightboxOpened}
              onClose={() => setLightboxOpened(false)}
              photos={lightboxPhotos}
              initialIndex={lightboxIndex}
            />

            {/* Delete Confirmation Modal */}
            <Modal
              opened={deleteModalOpened}
              onClose={() => {
                setDeleteModalOpened(false)
                setPhotoToDelete(null)
              }}
              title={content?.photos.deleteModalTitle || 'Foto löschen'}
              centered
              aria-labelledby="delete-modal-title"
            >
              <Stack gap="md">
                <Text>
                  {content?.photos.deleteModalText ||
                    'Möchten Sie dieses Foto wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'}
                </Text>
                {photoToDelete && (
                  <Text size="sm" c="dimmed">
                    {photoToDelete.filename}
                  </Text>
                )}
                <Group justify="flex-end" mt="md">
                  <Button
                    variant="subtle"
                    onClick={() => {
                      setDeleteModalOpened(false)
                      setPhotoToDelete(null)
                    }}
                    autoFocus
                  >
                    {content?.ui.Abbrechen || 'Abbrechen'}
                  </Button>
                  <Button color="red" onClick={handleConfirmDelete}>
                    {content?.photos.deleteButton || 'Löschen'}
                  </Button>
                </Group>
              </Stack>
            </Modal>
          </Stack>
        </Paper>
      </Container>
    </>
  )
}
